export type SlugifyOptions = {
  /** Hard cap on the slug length; the cut never leaves a trailing dash. */
  maxLength?: number
  /** Returned when nothing alphanumeric survives (default `''`). */
  fallback?: string
  /**
   * How non-ASCII input is treated. `'transliterate'` (default) NFKD-strips
   * diacritics and maps æ/œ/ø/ß/ð/þ/ł/đ (`Café` → `cafe`). `'separate'`
   * reproduces the pre-2026-09 pipelines, where every non-ASCII character
   * was just another separator (`Café` → `caf`) — required wherever the
   * slug is a key persisted OUTSIDE this repo (an Iterable client template
   * id, a HubSpot path, a public JSON-LD `@id`, a brand style id) and a
   * changed key would create a second record instead of updating the first.
   */
  nonAscii?: 'transliterate' | 'separate'
}

const NON_ASCII_RE = /[^\x20-\x7e]/g

const COMBINING_MARKS_RE = /[̀-ͯ]/g
const NON_ALPHANUMERIC_RUN_RE = /[^a-z0-9]+/g
const EDGE_DASHES_RE = /^-+|-+$/g
const TRAILING_DASHES_RE = /-+$/

/** Latin letters NFKD leaves intact but a URL slug should still keep. */
const LATIN_TRANSLITERATIONS: Readonly<Record<string, string>> = {
  æ: 'ae',
  œ: 'oe',
  ø: 'o',
  ß: 'ss',
  ð: 'd',
  þ: 'th',
  ł: 'l',
  đ: 'd',
}

const LATIN_TRANSLITERATION_RE = /[æœøßðþłđ]/g

/**
 * Turn free text into a URL/filename-safe slug: NFKD-strip diacritics
 * (`Café` → `cafe`, `Ærø` → `aero`), lowercase, collapse every
 * non-alphanumeric run to one `-`, trim edge dashes, then apply the
 * optional `maxLength` (re-trimming so a cut never ends in `-`), and
 * finally return `fallback` if nothing survived.
 *
 * Leaf module: zero imports, no `server-only` — safe from client
 * components, workflows, scripts, and `convex/`.
 *
 * Canonical for the URL-slug copies the 2026-09 condensation audit counted
 * (unit U3.4): `scripts/figma/email-detection.ts:slugify` (the only one
 * that already stripped diacritics), `app/(app)/assets/
 * brand-asset-attachment.ts:slugifyLabel` (cap 48, fallback `brand-asset`),
 * `lib/email/email-html-export.ts:slugifyEmailFilename`, `lib/email/audit/
 * provider-normalizers.ts:slug`, `scripts/figma/shared.ts:toFixtureSlug`,
 * `scripts/mailgun-eval/core.ts:slug` (which forgot to lowercase), and the
 * inline pipeline in `lib/contacts/export-csv.ts:exportFilenameFor`. Not for
 * `lib/intelligence/contracts.ts:slugify` (strips digits — an identity key,
 * not a URL), `slugifyStyleId`, `slugDomainGuess`, `slugifyTableName`, or
 * the camel→kebab `kebabCase` in the tiptap runtime.
 */
export function slugify(input: string, options: SlugifyOptions = {}): string {
  const { maxLength, fallback = '', nonAscii = 'transliterate' } = options
  if (
    maxLength !== undefined &&
    (!Number.isInteger(maxLength) || maxLength <= 0)
  ) {
    throw new RangeError(
      `slugify: maxLength must be a positive integer, got ${maxLength}`
    )
  }
  let slug = (
    nonAscii === 'separate' ? input.replace(NON_ASCII_RE, '-') : input
  )
    .normalize('NFKD')
    .replace(COMBINING_MARKS_RE, '')
    .toLowerCase()
    .replace(
      LATIN_TRANSLITERATION_RE,
      (char) => LATIN_TRANSLITERATIONS[char] ?? char
    )
    .replace(NON_ALPHANUMERIC_RUN_RE, '-')
    .replace(EDGE_DASHES_RE, '')
  if (maxLength !== undefined && slug.length > maxLength) {
    slug = slug.slice(0, maxLength).replace(TRAILING_DASHES_RE, '')
  }
  return slug || fallback
}
